import type {DataNodes, MeshNodes} from "@cm/render-nodes"
import {defaultMaterialParameters} from "@cm/render-nodes"
import type {AbstractMeshData} from "#template/interfaces/mesh-data"
import type {SceneNodes} from "#template/interfaces/scene-object"
import type {ToneMapping} from "#template/nodes/types"
import type {ObjectPath} from "#template/template-node/types"
import type {TemplateNode} from "#template/template-node/template-node"
import {getNodeOwner, getOwnedNodes, isNamedNode} from "#template/utils/template-node-utils"
import {Group} from "#template/nodes/owner/group"
import {TemplateNodes} from "#template/nodes/template-nodes"
import {MaterialReference} from "#template/nodes/material/material-reference"
import type {TemplateGraph} from "#template/nodes/template-graph/template-graph"
import {isInstanceOf} from "@cm/utils"
import {areaLightWatts, pointLightWatts} from "#template/utils/light-intensity"
import {getMeshGraphInfo} from "#template/utils/render-graph-info"
import {toBlenderLength, toBlenderTransform} from "#template/utils/blender-coordinates"
import {removeDuplicates, simplifyControlPoints} from "#template/utils/curve-simplification"
import type {Vector3} from "@cm/math"
import type {JsonObject} from "@cm/utils/type"
import type {MaterialGraph, UvOffset} from "@cm/material-nodes"
import {Mapping, MaterialConnection, MaterialNode, TexImage} from "@cm/material-nodes"
import type {ImageColorSpace} from "@cm/utils/data-object"
import {dataObjectImageNode} from "#template/nodes/image/data-object-reference"
import {transformDecalMask} from "#template/nodes/object/mesh-decal"
import {transformColorOverlay} from "#template/runtime-graph/nodes/transform-color-overlay"

export type BlenderMeshSource = {
    graph: MeshNodes.Mesh
    renderLevels: number
    viewportLevels: number
    dataObjectReferences: DataNodes.DataObjectReference[]
}

export type BlenderMaterialSlot = {
    name: string
    materialLegacyId: number | null
    materialRevisionLegacyId: number | null
    // set where the template changed the material from its revision, whose own material the slot then must not take
    templateMaterialKey: string | null
    defaultMaterial: {color: [number, number, number]; roughness: number; metalness: number}
}

// what the web app sends the addon, one object per scene node
type BlenderObjectBase = {
    name: string
    // the names of the collections it sits in, outermost first
    collections: string[]
    matrixWorld: number[]
}

// attributes keep their own indices, as in the add_mesh message the addon sends
export type BlenderMeshObject = BlenderObjectBase & {
    type: "mesh"
    polyVertexCount: Uint32Array
    vertices: Float32Array
    vertexIndices: Uint32Array
    normals: Float32Array
    normalIndices: Uint32Array
    uvs: Float32Array[]
    uvIndices: Uint32Array[]
    materialIds: Uint32Array
    materialIdIndices: Uint32Array
    subdivision: {viewportLevels: number; renderLevels: number}
    // the template makes a decal from the mesh it lies on, so Blender leaves it out of an upload
    decal: boolean
    materialSlots: BlenderMaterialSlot[]
}

export type BlenderCurveSpline = {
    points: Float32Array
    // left and right handle of each point, which make every segment the platform's own Catmull-Rom segment
    handles: Float32Array
    // 1 where the platform splits the curve into pieces meeting at a sharp corner
    corners: Uint8Array
    closed: boolean
}

// points on Blender's own local axes, the way the curve import reads them back, with matrixWorld converting the axes
export type BlenderCurveObject = BlenderObjectBase & {
    type: "curve"
    splines: BlenderCurveSpline[]
    // the curve's mesh among the objects sent with it, which Blender wraps it onto as the platform casts it onto the mesh; null when not sent
    meshIndex: number | null
}

// what the addon's upload reads back as add_light, add_point_light, add_sun_light and add_light_portal
export type BlenderLightObject = BlenderObjectBase & {
    type: "light"
    kind: "AREA" | "POINT" | "SUN" | "PORTAL"
    color: [number, number, number]
    // watts, and for a sun watts per square metre, as Blender measures them
    power: number
    width: number
    height: number
    radius: number
    angle: number
    spread: number
    visibleToCamera: boolean
    on: boolean
}

export type BlenderCameraObject = BlenderObjectBase & {
    type: "camera"
    lens: number
    sensorWidth: number
    // null where the platform renders without depth of field
    fStop: number | null
    focusDistance: number
    shiftX: number
    shiftY: number
    clipStart: number | null
    clipEnd: number | null
    resolution: [number, number]
    // the renderer's film exposure, 2 to the power of the camera's ev
    exposure: number
    // what the configurator and interactive rendering show
    toneMapping: ToneMapping
}

export type BlenderRenderSettings = {
    samples: number
    percentage: number
}

// the template's Post Process node, which offline renders go through
export type BlenderPostProcess = {
    ev: number
    whiteBalance: number | null
    toneMapping: ToneMapping | null
    lutUrl: string | null
}

export type BlenderObject = BlenderMeshObject | BlenderCurveObject | BlenderLightObject | BlenderCameraObject

// an uploaded image, in the selection of the data object the addon downloads a map by
export type BlenderDecalImage = {
    id: string
    legacyId: number
    originalFileName: string
    downloadUrl: string
    imageColorSpace: ImageColorSpace | null
}

// what the platform's decal puts in front of its material's base colour and alpha
export type BlenderDecal = {
    widthCm: number
    heightCm: number
    mask: BlenderDecalImage | null
    invertMask: boolean
    colorOverlay: BlenderDecalImage | null
    // the platform leaves out what is less opaque than this
    alphaThreshold: number
}

// a platform material as the browser's session fetched it, in the shapes the addon's own queries answer with
export type BlenderMaterial = {
    revisionLegacyId: number
    templateMaterialKey: string | null
    // what the template changed the revision by, for a mesh uploaded back with this material to assign it the same
    templateOffset: UvOffset | null
    decal: BlenderDecal | null
    materialRevision: JsonObject
    textureRevisions: JsonObject[]
    textureSetRevisions: JsonObject[]
}

// a platform HDRI with the settings of the light showing it; the rotation is already Blender's world Mapping's
export type BlenderHdri = {
    name: string
    hdriLegacyId: number
    hdriName: string
    dataObject: {id: string; originalFileName: string; downloadUrl: string}
    rotation: [number, number, number]
    mirror: boolean
    intensity: number
    saturation: number
}

// without materials, the zones get the platform's default material for their slot
export type BlenderImportObjectsMessage = {
    type: "import_objects"
    // the GraphQL server the materials came from, whose revision numbers mean nothing on another
    environment: string
    objects: BlenderObject[]
    materials: BlenderMaterial[]
    hdris: BlenderHdri[]
    render: BlenderRenderSettings | null
    postProcess: BlenderPostProcess | null
}

// shown in Blender's status bar while the web app prepares an import; null clears it
export type BlenderStatusMessage = {
    type: "status"
    text: string | null
}

// a scene object's path holds only the template instances it sits in, not the groups and config variants that own each
// of them, which reach up to the template graph that has no owner of its own
export function blenderCollections(path: ObjectPath): string[] {
    const owners = (node: TemplateNode): TemplateNode[] => {
        const owner = getNodeOwner(node)
        return owner && getNodeOwner(owner) ? [...owners(owner), owner] : []
    }
    return path
        .flatMap((node, index) => [...owners(node), ...(index < path.length - 1 ? [node] : [])])
        .flatMap((node) => (isNamedNode(node) ? [node.parameters.name] : []))
}

// the size a decal's image is laid out at, which the Mapping in front of its image node scales the UVs by
function decalImageSize(graph: MaterialGraph, dataObjectId: string): [number, number] | null {
    const visited = new Set<MaterialNode>()
    const visit = (node: MaterialNode): [number, number] | null => {
        if (visited.has(node)) return null
        visited.add(node)
        if (node instanceof TexImage && node.parameters.settings.dataObjectId === dataObjectId) {
            const mapping = node.parameters.inputs.vector?.parameters.value
            const scale = mapping instanceof Mapping ? mapping.parameters.settings.scale : undefined
            if (scale) return [scale.x, scale.y]
        }
        for (const input of Object.values(node.parameters.inputs)) {
            const found = input instanceof MaterialConnection && input.parameters.value instanceof MaterialNode ? visit(input.parameters.value) : null
            if (found) return found
        }
        return null
    }
    return graph.rootNode ? visit(graph.rootNode) : null
}

// the decal material the platform makes of revision, at the size decalGraph lays its images out at, for the caller to
// confirm against decalGraph that the decal is all the template changed
export function rebuildDecal(
    revision: MaterialGraph,
    decalGraph: MaterialGraph,
    {mask, colorOverlay, invertMask}: Pick<BlenderDecal, "mask" | "colorOverlay" | "invertMask">,
): {graph: MaterialGraph; widthCm: number; heightCm: number} {
    const image = mask ?? colorOverlay
    const [widthCm, heightCm] = (image && decalImageSize(decalGraph, image.id)) ?? [0, 0]
    const size: [number, number] = [widthCm, heightCm]
    const maskImage = mask ? dataObjectImageNode(mask) : undefined
    const colorOverlayImage = colorOverlay ? dataObjectImageNode(colorOverlay) : undefined
    const overlaid = colorOverlayImage ? transformColorOverlay(revision, colorOverlayImage, size, false) : revision
    return {graph: transformDecalMask(overlaid, {maskImage, colorOverlayImage, widthCm, heightCm, invert: invertMask}), widthCm, heightCm}
}

// the nodes of the group a Blender collection path leads to, reusing a group of the same name at each level, as the
// collections the template sends to Blender are named after its groups
export function nodesAlongCollections(graph: TemplateGraph, collections: string[]): TemplateNodes {
    let nodes = graph.parameters.nodes
    for (const name of collections) {
        let group = nodes.parameters.list.find((node): node is Group => node instanceof Group && node.parameters.name === name)
        if (!group) {
            group = new Group({parameters: {name, nodes: new TemplateNodes({parameters: {list: []}}), active: true}})
            nodes.addEntry(group)
        }
        nodes = group.parameters.nodes
    }
    return nodes
}

// a reference in a config variant or template instance goes when that goes, so a mesh from Blender does not share it
export function sharedMaterialReferences(graph: TemplateGraph) {
    const inGroups = (node: TemplateNode): TemplateNode[] => (node instanceof Group ? getOwnedNodes(node).flatMap(inGroups) : [node])
    const known = getOwnedNodes(graph).flatMap(inGroups).filter(isInstanceOf(MaterialReference))
    const added: MaterialReference[] = []
    const referenceFor = ({materialId, materialRevisionId, name}: {materialId: number; materialRevisionId: number | null; name: string}): MaterialReference => {
        const existing = known.find(({parameters}) => parameters.materialId === materialId && parameters.materialRevisionId === materialRevisionId)
        if (existing) return existing
        const reference = new MaterialReference({parameters: {name, materialId, materialRevisionId}})
        known.push(reference)
        added.push(reference)
        return reference
    }
    // after everything the upload placed, so the template lists the objects first and the references it added below them
    const appendNew = () => {
        for (const reference of added.splice(0)) graph.parameters.nodes.addEntry(reference)
    }
    return {referenceFor, appendNew}
}

// one slot per material index, so Blender's material_index keeps the platform's material zones
export function blenderMaterialSlots(
    materialMap: SceneNodes.Mesh["materialMap"],
    templateMaterialKey: (material: NonNullable<ReturnType<SceneNodes.Mesh["materialMap"]["get"]>>) => string | null = () => null,
): BlenderMaterialSlot[] {
    const count = Math.max(-1, ...materialMap.keys()) + 1
    return Array.from({length: count}, (_, index) => {
        const material = materialMap.get(index) ?? null
        const {color, roughness, metalness} = defaultMaterialParameters(index)
        return {
            name: material?.name ?? `Zone ${index + 1}`,
            materialLegacyId: material?.materialGraph.parameters.materialLegacyId ?? null,
            materialRevisionLegacyId: material?.materialGraph.parameters.materialRevisionLegacyId ?? null,
            templateMaterialKey: material ? templateMaterialKey(material) : null,
            defaultMaterial: {color: [...color], roughness, metalness},
        }
    })
}

// the tolerance the curve import reduces to, so a curve sent back from Blender reduces to about the points it went out with
const curveTolerance = 0.03

type CurveEntry = {point: Vector3; valid: boolean; corner: boolean}

export type MeshCurveProjection = {hitPoints: Vector3[]; valid: boolean[]; segments: ArrayLike<number>}

// the platform samples each piece between corners from half a step in, so a corner is put back where the samples pass it;
// a closed curve's samples end with the first one again, so its closing stretch is passed like any other
function withCorners({hitPoints, valid, segments}: MeshCurveProjection, controlPoints: {position: Vector3; corner: boolean}[]) {
    const corners = controlPoints.flatMap((controlPoint, index) => (controlPoint.corner ? [index] : []))
    const cornersBetween = (from: number, to: number): CurveEntry[] =>
        corners
            .filter((index) => (from < to ? from < index && index <= to : from < index || index <= to))
            .map((index) => ({point: controlPoints[index]!.position, valid: true, corner: true}))

    const entries: CurveEntry[] = []
    hitPoints.forEach((point, index) => {
        if (index > 0) entries.push(...cornersBetween(segments[index - 1]!, segments[index]!))
        entries.push({point, valid: valid[index]!, corner: false})
    })
    return entries
}

// each piece between corners is reduced on its own, so a corner stays where it is
function reduced(run: CurveEntry[]): CurveEntry[] {
    const pieces: CurveEntry[][] = [[]]
    for (const entry of run) {
        pieces[pieces.length - 1]!.push(entry)
        if (entry.corner) pieces.push([entry])
    }
    const corners = new Set(run.filter((entry) => entry.corner).map((entry) => entry.point))
    return pieces
        .filter((piece) => piece.length > 1)
        .flatMap((piece, index) => {
            const points = simplifyControlPoints(removeDuplicates(piece.map((entry) => entry.point)), curveTolerance)
            return (index === 0 ? points : points.slice(1)).map((point) => ({point, valid: true, corner: corners.has(point)}))
        })
}

// the Bezier handles of a centripetal Catmull-Rom segment, from the tangents three.js gives it
function segmentHandles(p0: Vector3, p1: Vector3, p2: Vector3, p3: Vector3) {
    let dt1 = Math.sqrt(p1.distance(p2))
    if (dt1 < 1e-4) dt1 = 1
    const dt0 = Math.sqrt(p0.distance(p1)) < 1e-4 ? dt1 : Math.sqrt(p0.distance(p1))
    const dt2 = Math.sqrt(p2.distance(p3)) < 1e-4 ? dt1 : Math.sqrt(p2.distance(p3))
    const tangent1 = p1
        .sub(p0)
        .mul(1 / dt0)
        .sub(p2.sub(p0).mul(1 / (dt0 + dt1)))
        .add(p2.sub(p1).mul(1 / dt1))
        .mul(dt1)
    const tangent2 = p2
        .sub(p1)
        .mul(1 / dt1)
        .sub(p3.sub(p1).mul(1 / (dt1 + dt2)))
        .add(p3.sub(p2).mul(1 / dt2))
        .mul(dt1)
    return {right: p1.add(tangent1.mul(1 / 3)), left: p2.sub(tangent2.mul(1 / 3))}
}

// a piece of the platform's curve ends at a corner and at an open end, where three.js extrapolates the missing neighbour
function catmullRomHandles(points: Vector3[], corners: boolean[], closed: boolean) {
    const count = points.length
    const left: Vector3[] = [...points]
    const right: Vector3[] = [...points]
    for (let i = 0; i < (closed ? count : count - 1); i++) {
        const j = (i + 1) % count
        const startsPiece = corners[i] || (!closed && i === 0)
        const endsPiece = corners[j] || (!closed && j === count - 1)
        const before = startsPiece ? points[i]!.mul(2).sub(points[j]!) : points[(i - 1 + count) % count]!
        const after = endsPiece ? points[j]!.mul(2).sub(points[i]!) : points[(j + 1) % count]!
        const handles = segmentHandles(before, points[i]!, points[j]!, after)
        right[i] = handles.right
        left[j] = handles.left
    }
    if (!closed) {
        left[0] = points[0]!.mul(2).sub(right[0]!)
        right[count - 1] = points[count - 1]!.mul(2).sub(left[count - 1]!)
    }
    return points.map((_, i) => [left[i]!, right[i]!])
}

const inBlenderUnits = (points: Vector3[]) => new Float32Array(points.flatMap(({x, y, z}) => [toBlenderLength(x), toBlenderLength(y), toBlenderLength(z)]))

// a sample whose ray missed the mesh leaves a gap, so each run of hits becomes a spline, and only an unbroken closed curve stays closed
export function blenderCurveSplines(
    projection: MeshCurveProjection,
    controlPoints: {position: Vector3; corner: boolean}[],
    closed: boolean,
): BlenderCurveSpline[] {
    const entries = withCorners(projection, controlPoints)
    const runs: CurveEntry[][] = []
    entries.forEach((entry, index) => {
        if (!entry.valid) return
        if (index > 0 && entries[index - 1]!.valid) runs[runs.length - 1]!.push(entry)
        else runs.push([entry])
    })
    const unbroken = closed && entries.every((entry) => entry.valid)
    if (closed && !unbroken && runs.length > 1 && entries[0]!.valid && entries[entries.length - 1]!.valid) runs[0] = [...runs.pop()!, ...runs[0]!]

    return runs.flatMap((run) => {
        let spline = reduced(run)
        if (unbroken && spline.length > 2 && spline[0]!.point.equals(spline[spline.length - 1]!.point)) spline = spline.slice(0, -1)
        if (spline.length < 2) return []
        const points = spline.map(({point}) => point)
        const corners = spline.map(({corner}) => corner)
        return [
            {
                points: inBlenderUnits(points),
                handles: inBlenderUnits(catmullRomHandles(points, corners, unbroken).flat()),
                corners: new Uint8Array(corners.map((corner) => (corner ? 1 : 0))),
                closed: unbroken,
            },
        ]
    })
}

// the outermost subdivision becomes Blender's Subsurf modifier; subdivision nested deeper stays in the exported geometry
export function blenderMeshSource({
    renderGeometryGraph,
    displayGeometryGraph,
}: Pick<AbstractMeshData, "renderGeometryGraph" | "displayGeometryGraph">): BlenderMeshSource {
    const graph = renderGeometryGraph.type === "subdivide" ? renderGeometryGraph.input : renderGeometryGraph
    return {
        graph,
        renderLevels: renderGeometryGraph.type === "subdivide" ? renderGeometryGraph.levels : 0,
        viewportLevels: displayGeometryGraph.type === "subdivide" ? displayGeometryGraph.levels : 0,
        dataObjectReferences: getMeshGraphInfo(graph).dataObjectReferences,
    }
}

// the upload reads a camera without depth of field as this f-stop
const noDepthOfField = 128

type Placed<T> = Omit<T, "name" | "collections">

// the inverse of how the platform renders a light the upload created, which for a Corona light gives the watts that render as bright
export function blenderLight(light: SceneNodes.AreaLight | SceneNodes.PointLight | SceneNodes.SunLight | SceneNodes.LightPortal): Placed<BlenderLightObject> {
    const common = {type: "light" as const, width: 0, height: 0, radius: 0, angle: 0, spread: 0, color: [1, 1, 1] as [number, number, number]}
    switch (light.type) {
        case "AreaLight":
            return {
                ...common,
                kind: "AREA",
                matrixWorld: toBlenderTransform(light.transform, true),
                color: [...light.color],
                power: areaLightWatts(light.intensity, light.width, light.height),
                width: toBlenderLength(light.width),
                height: toBlenderLength(light.height),
                spread: (1 - light.directionality) * Math.PI,
                visibleToCamera: light.visibleDirectly,
                on: light.on,
            }
        case "PointLight":
            return {
                ...common,
                kind: "POINT",
                matrixWorld: toBlenderTransform(light.transform, false),
                color: [...light.color],
                power: pointLightWatts(light.intensity, light.radius),
                radius: toBlenderLength(light.radius),
                visibleToCamera: light.visibleDirectly,
                on: light.on,
            }
        case "SunLight":
            return {
                ...common,
                kind: "SUN",
                matrixWorld: toBlenderTransform(light.transform, true),
                color: [...light.color],
                power: light.intensity,
                angle: (light.angle * Math.PI) / 180,
                visibleToCamera: light.visibleDirectly,
                on: light.on,
            }
        case "LightPortal":
            return {
                ...common,
                kind: "PORTAL",
                matrixWorld: toBlenderTransform(light.transform, true),
                power: 0,
                width: toBlenderLength(light.width),
                height: toBlenderLength(light.height),
                visibleToCamera: false,
                on: true,
            }
    }
}

// the inverse of the upload, which scales Blender's shift by the render's aspect before the platform stores it
export function blenderCamera(camera: SceneNodes.Camera): Placed<BlenderCameraObject> {
    const {resolutionX: x, resolutionY: y} = camera
    return {
        type: "camera",
        matrixWorld: toBlenderTransform(camera.transform, true),
        lens: camera.focalLength,
        sensorWidth: camera.filmGauge,
        fStop: camera.fStop >= noDepthOfField ? null : camera.fStop,
        focusDistance: toBlenderLength(camera.focalDistance),
        shiftX: camera.shiftX / (y > x ? y / x : 1),
        shiftY: camera.shiftY / (x > y ? x / y : 1),
        clipStart: camera.nearClip === undefined ? null : toBlenderLength(camera.nearClip),
        clipEnd: camera.farClip === undefined ? null : toBlenderLength(camera.farClip),
        resolution: [x, y],
        exposure: camera.exposure,
        toneMapping: camera.realtimeSettings.toneMapping,
    }
}
